import { useRef, useState } from "react";
import { toast } from "sonner";

import { useAssignTasksToTrip, useCarrierTrips } from "@/api/carrier";
import { Button } from "@/components/ui/button";
import { Dialog, type DialogProps } from "@/components/ui/dialog";
import { NativeSelect } from "@/components/ui/native-select";
import { operatorDisplayName } from "@/lib/operator-identity";
import { hasHttpStatus } from "@/lib/api-error";

interface AssignTaskToTripDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  taskId: string;
  taskLabel: string;
  dialogPresentation?: DialogProps["presentation"];
}

/**
 * Pick a target trip and POST `/trips/:id/assign-tasks` for a single task.
 * Trip options come from the scoped trips GET; the server still re-checks
 * that the task and trip share a dispatch center before grouping it into a
 * waypoint.
 */
export function AssignTaskToTripDialog({
  open,
  onOpenChange,
  taskId,
  taskLabel,
  dialogPresentation,
}: AssignTaskToTripDialogProps) {
  const { data: tripsPage, isLoading, error } = useCarrierTrips({ limit: 100 });
  const assign = useAssignTasksToTrip();
  const [tripId, setTripId] = useState<string>("");
  const submitLock = useRef(false);
  const responsive = dialogPresentation === "responsive-sheet";

  // Only PLANNED/ACTIVE trips can take more tasks.
  const openTrips = (tripsPage?.data ?? []).filter(
    (t) => t.status === "PLANNED" || t.status === "ACTIVE"
  );

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!tripId || submitLock.current) return;
    submitLock.current = true;
    try {
      await assign.mutateAsync({ tripId, taskIds: [taskId] });
      toast.success(`${taskLabel} assigned to trip.`);
      onOpenChange(false);
      setTripId("");
    } catch {
      toast.error("Failed to assign task to trip.");
      // also surfaced inline below
    } finally {
      submitLock.current = false;
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        if (!assign.isPending) onOpenChange(nextOpen);
      }}
      title="Assign to Trip"
      presentation={dialogPresentation}
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label
            htmlFor="task-trip-picker"
            className="block text-sm font-medium mb-1"
          >
            Target trip
          </label>
          <NativeSelect
            id="task-trip-picker"
            value={tripId}
            onChange={(e) => setTripId(e.target.value)}
            disabled={isLoading || assign.isPending}
          >
            <option value="">Select trip...</option>
            {openTrips.map((trip) => (
              <option key={trip.id} value={trip.id}>
                TRP-{trip.tripNumber} ·{" "}
                {trip.operator
                  ? operatorDisplayName(trip.operator)
                  : "Unassigned"}{" "}
                · {trip.status} ·{" "}
                {trip.dispatchCenter?.name ?? "No Dispatch Center"}
              </option>
            ))}
          </NativeSelect>
        </div>

        {error && (
          <p role="alert" className="text-sm text-destructive">
            {hasHttpStatus(error, 403)
              ? "You do not have permission to load Trips."
              : "Failed to load Trips. Try again."}
          </p>
        )}

        {!isLoading && !error && openTrips.length === 0 && (
          <p className="text-sm text-muted-foreground">
            No open trips. Create a trip first.
          </p>
        )}

        {assign.isError && (
          <p role="alert" className="text-sm text-destructive">
            Failed to assign. The task must share the trip's dispatch center.
          </p>
        )}

        <div
          className={
            responsive
              ? "sticky bottom-0 grid grid-cols-2 gap-2 border-t bg-background pt-4"
              : "flex justify-end gap-2"
          }
        >
          <Button
            type="button"
            variant="outline"
            size={responsive ? "touch" : "default"}
            onClick={() => onOpenChange(false)}
            disabled={assign.isPending}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            size={responsive ? "touch" : "default"}
            disabled={!tripId || assign.isPending || !!error}
          >
            {assign.isPending ? "Assigning..." : "Assign"}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
